import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { Iconify, Label } from '@/components';
import { Referral, ReferralRewardDisplay } from '../types';
import {
  classifyReferralRewards,
  getReferralProgress,
  getReferralUserDisplayName,
  getReferralInviter,
  getReferralInvitee,
} from '../utils';

type RewardCouponListProps = {
  title: string;
  ownerLabel: string;
  coupons: ReferralRewardDisplay[];
};

const formatCouponValue = (coupon: ReferralRewardDisplay) => {
  if (coupon.value === undefined || coupon.value === null) return null;

  return coupon.valueType?.toLowerCase() === 'percentage'
    ? `${coupon.value}%`
    : `£${coupon.value}`;
};

function RewardCouponList({
  title,
  ownerLabel,
  coupons,
}: RewardCouponListProps) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {coupons.length ? (
        <Stack spacing={1}>
          {coupons.map((coupon) => (
            <Box
              key={`${ownerLabel}-${coupon.id ?? coupon.code}`}
              sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                    {coupon.code}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {coupon.description || coupon.sourceLabel || ownerLabel}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {formatCouponValue(coupon) && (
                    <Label color="info">{formatCouponValue(coupon)}</Label>
                  )}
                  {coupon.active !== null && coupon.active !== undefined && (
                    <Label color={coupon.active ? 'success' : 'default'}>
                      {coupon.active ? 'Active' : 'Inactive'}
                    </Label>
                  )}
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No voucher details returned.
        </Typography>
      )}
    </Box>
  );
}

export default function ReferralRewardDetails({
  referral,
}: {
  referral: Referral;
}) {
  const progress = getReferralProgress(referral);
  const rewards = classifyReferralRewards(referral);
  const inviterName = getReferralUserDisplayName(
    getReferralInviter(referral),
    'Inviter'
  );
  const inviteeName = getReferralUserDisplayName(
    getReferralInvitee(referral),
    referral.invitee_email || 'Invitee'
  );
  const isReadyButNotRewarded =
    !progress.isRewarded && progress.completed >= progress.required;

  return (
    <Stack spacing={2.5}>
      {isReadyButNotRewarded && (
        <Alert
          severity="info"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                disabled
                startIcon={<Iconify icon="eva:gift-outline" />}
              >
                Manual reward
              </Button>
              <Button
                size="small"
                disabled
                startIcon={<Iconify icon="eva:refresh-fill" />}
              >
                Retry reward
              </Button>
            </Stack>
          }
        >
          This referral is ready, but the dashboard has no reward mutation API
          yet. Reward generation remains backend work.
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems="stretch"
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <RewardCouponList
            title="Inviter voucher"
            ownerLabel={inviterName}
            coupons={rewards.inviter}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <RewardCouponList
            title="Invitee voucher"
            ownerLabel={inviteeName}
            coupons={rewards.invitee}
          />
        </Box>
      </Stack>

      {rewards.references.length > 0 && (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Reward reference info
            </Typography>
            <Stack spacing={0.75}>
              {rewards.references.map((reference) => (
                <Stack
                  key={`${reference.label}-${reference.value}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent="space-between"
                >
                  <Typography variant="caption" color="text.secondary">
                    {reference.label}
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {reference.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
}
